import React, { useRef, useState } from "react";

import { AddCircleFilled } from "@toddle-edu/ds-icons";
import classNames from "classnames";

import { Popover } from "@toddle-edu/react-tiny-popover";

import classes from "./AddLine.scss";

const OFFSET = 12;

const ADD_LINE = "ADD_LINE"; // do not change this id

const styles = {
  popoverContainerStyle: {
    zIndex: 2001,
    left: "-8px",
  },
};

const AddLine = props => {
  const {
    id,
    onAdd,
    isVisible = true,
    type = "column",
    style,
    isHover = false,
    testId,
    centerAlways = true, // whether button is always centered
  } = props;

  const [position, setPosition] = useState(0);
  const [showAddIcon, setShowAddIcon] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const lineRef = useRef(null);

  // Calculate center position when focused
  const getCenteredPosition = () => {
    if (lineRef.current) {
      const rect = lineRef.current.getBoundingClientRect();
      return type === "column" ? rect.height / 2 : rect.width / 2;
    }

    return 0;
  };

  const handleMouseMove = e => {
    if (lineRef.current) {
      const rect = lineRef.current.getBoundingClientRect();
      let newPosition;

      if (type === "column") {
        newPosition = e.clientY - rect.top;
        if (newPosition < 0) newPosition = 0;
        if (newPosition > rect.height) newPosition = rect.height;
      } else {
        newPosition = e.clientX - rect.left;
        if (newPosition < 0) newPosition = 0;
        if (newPosition > rect.width) newPosition = rect.width;
      }

      setPosition(newPosition);
    }
  };

  const onMouseEnter = () => {
    setShowAddIcon(true);

    if (isHover && lineRef.current) {
      lineRef.current.style.backgroundColor = "var(--border-semantic-info)";
    }
  };

  const onMouseLeave = () => {
    // Only hide if not focused by keyboard
    if (!isFocused) {
      setShowAddIcon(false);
    }

    if (isHover && lineRef.current && !isFocused) {
      lineRef.current.style.backgroundColor = "var(--surface-secondary-active)";
    }
  };

  const onFocus = () => {
    setIsFocused(true);
    setShowAddIcon(true);
  };

  const onBlur = () => {
    setIsFocused(false);
    setShowAddIcon(false);

    if (isHover && lineRef.current) {
      lineRef.current.style.backgroundColor = "var(--surface-secondary-active)";
    }
  };

  const onKeyDown = e => {
    // Handle enter or space key to trigger the add action
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onAdd();
    }

    props.onKeyDown?.(e);
  };

  const containerClass = classNames({
    [classes.line]: true,
    [classes.hoverLine]: isHover,
    [classes[type]]: true,
    [classes.hidden]: !isVisible,
  });

  // Determine if popover should be visible based on hover or keyboard focus
  const shouldShowAddIcon = showAddIcon || isFocused;

  // Use center position when focused or centerAlways is true, otherwise use mouse position
  const displayPosition =
    isFocused || centerAlways ? getCenteredPosition() : position;

  const buttonStyle =
    type === "column"
      ? { top: `${displayPosition - OFFSET}px` }
      : { left: `${displayPosition - OFFSET}px` };

  return (
    <div
      id={id ? `${ADD_LINE}_${id}` : ADD_LINE}
      ref={lineRef}
      className={containerClass}
      onMouseMove={centerAlways ? undefined : handleMouseMove}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      onClick={onAdd}
      style={style}
      tabIndex={0}
      role="button"
      aria-label="Add new item"
    >
      {shouldShowAddIcon ? (
        <Popover
          isOpen={true}
          align={"center"}
          positions={["top", "bottom"]}
          containerStyle={styles.popoverContainerStyle}
          boundaryInset={20}
          reposition={true}
          content={() => {
            return (
              <AddCircleFilled
                variant={"success"}
                size={"xxx-small"}
                className={classes.addButton}
                style={buttonStyle}
                onClick={e => {
                  e.stopPropagation();
                  onAdd();
                }}
                data-test-id={testId}
              />
            );
          }}
          childrenRef={{ current: lineRef.current }}
          isChildrenRefPassed={true}
        />
      ) : null}
    </div>
  );
};

export default AddLine;
